import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { queryKeys } from '@/lib/queryKeys';

export interface UserProfile {
  id: string;
  user_id: string;
  display_name: string | null;
  avatar_url: string | null;
  auth_provider: string | null;
  is_active: boolean | null;
  created_at: string;
  updated_at: string;
}

export function useUserProfile(userId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.userProfile(userId || ''),
    queryFn: async () => {
      if (!userId) return null;
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error) {
        console.error('Error fetching user profile:', error);
        return null;
      }

      return data as UserProfile;
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useUserProfiles(userIds: string[]) {
  return useQuery({
    queryKey: queryKeys.userProfiles(userIds.sort().join(',')),
    queryFn: async () => {
      if (!userIds.length) return {};

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .in('user_id', userIds);

      if (error) {
        console.error('Error fetching user profiles:', error);
        return {};
      }

      // Convert array to object keyed by user_id
      const profilesMap: { [userId: string]: UserProfile } = {};
      data.forEach(profile => {
        profilesMap[profile.user_id] = profile;
      });

      return profilesMap;
    },
    enabled: userIds.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}